import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Check,
  Download,
  FileUp,
  History,
  Loader2,
  Merge,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/components/Confirm";
import { ACCENT_SWATCHES, useSettings } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Mirrors backend server.py's FONT_OPTIONS — the sample and short blurb are
// display-only, the name is what's actually sent and saved.
const FONTS = [
  { name: "Plus Jakarta Sans", desc: "Default — clean and modern" },
  { name: "IBM Plex Sans", desc: "Technical, engineered" },
  { name: "Syne", desc: "Bold and distinctive" },
  { name: "Inter", desc: "Neutral and versatile" },
  { name: "Outfit", desc: "Geometric and friendly" },
  { name: "Space Grotesk", desc: "Modern with character" },
  { name: "DM Sans", desc: "Low-contrast and clean" },
  { name: "Raleway", desc: "Elegant and light" },
  { name: "Nunito", desc: "Rounded and approachable" },
  { name: "Playfair Display", desc: "Classic serif, high contrast" },
  { name: "Libre Baskerville", desc: "Traditional book serif" },
  { name: "IBM Plex Mono", desc: "Monospace, technical" },
];

function Section({ title, hint, children, hidden }) {
  if (hidden) return null;
  return (
    <div className="panel panel-pad">
      <div className="eyebrow">{title}</div>
      {hint && <p className="text-[12.5px] text-muted2 mt-1.5 max-w-lg">{hint}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

const SNAPSHOT_LOOK = {
  replace: { icon: Upload, tone: "bg-rose-50 text-stock-out" },
  client_delete: { icon: Trash2, tone: "bg-rose-50 text-stock-out" },
  item_merge: { icon: Merge, tone: "bg-gold-light text-gold" },
  manual: { icon: History, tone: "bg-surface text-muted2" },
};

function timeAgo(value) {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return formatDate(value);
}

/** "3 messages fewer now" — the difference between a copy and how things stand. */
function describeChanges(changes) {
  if (!changes) return "";
  const words = { conversations: "conversation", messages: "message" };
  const parts = Object.entries(changes)
    .filter(([, n]) => n)
    .map(([key, n]) => {
      const word = words[key] || key;
      const count = Math.abs(n);
      return `${count} ${word}${count === 1 ? "" : "s"} ${n > 0 ? "fewer" : "more"} now`;
    });
  return parts.slice(0, 2).join(" · ");
}

export default function Settings() {
  const confirm = useConfirm();
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const loadSnapshots = useCallback(async () => {
    try {
      const { data } = await api.get("/snapshots");
      setSnapshots(data.snapshots || []);
    } catch {
      setSnapshots([]);
    }
  }, []);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        business_name: form.business_name,
        brand_color: form.brand_color,
        font_family: form.font_family,
        dark_mode: !!form.dark_mode,
        pin_policy: form.pin_policy,
      });
      toast.success("Settings saved");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const takeSnapshot = async () => {
    try {
      await api.post("/snapshots");
      toast.success("Copy saved");
      loadSnapshots();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const restoreSnapshot = async (snap) => {
    const ok = await confirm({
      title: `Go back to ${formatDate(snap.at)}?`,
      body: "Everything here is replaced with how it was at that moment. A copy of right now is saved first, so this is reversible too.",
      confirmText: "Restore",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/snapshots/${snap.snapshot_id}/restore`);
      toast.success(`Restored ${data.restored.conversations ?? 0} conversations`);
      loadSnapshots();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const downloadBackup = async () => {
    try {
      const res = await api.get("/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const importBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ok = await confirm({
      title: "Replace everything with this backup?",
      body: "Every conversation and message currently here is replaced with what's in the file. A copy of what's here now is saved first, so this is reversible.",
      confirmText: "Import and replace",
      destructive: true,
    });
    if (!ok) return;
    setImporting(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/import", body);
      const counts = data.imported || {};
      const total = Object.values(counts).reduce((s, n) => s + n, 0);
      toast.success(`Imported ${total} record${total === 1 ? "" : "s"}`);
      loadSnapshots();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };

  const resetAll = async () => {
    const ok = await confirm({
      title: "Delete everything?",
      body: "Every conversation and message goes. Your team accounts stay. This can't be undone.",
      confirmText: "Delete everything",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete("/admin/reset");
      toast.success("All data cleared");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Settings"
      subtitle="Business details and preferences"
      action={
        <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-hover">
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Save changes</span>
            </>
          )}
        </Button>
      }
    >
      <Tabs defaultValue="general" className="max-w-5xl">
        <TabsList className="h-auto p-1 grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
          <TabsTrigger value="general" className="px-4 py-1.5">General</TabsTrigger>
          <TabsTrigger value="storage" className="px-4 py-1.5">Data &amp; Storage</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-5">
            <Section title="Business" hint="Shown in the sidebar and on the sign-in screen.">
              <div className="space-y-1.5">
                <Label htmlFor="bn">Business name</Label>
                <Input
                  id="bn"
                  value={form.business_name || ""}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                />
              </div>
            </Section>

            <Section
              title="Appearance"
              hint="The accent color used for buttons, links and highlights across the app."
            >
              <div className="space-y-1.5">
                <Label>Accent color</Label>
                <div className="flex flex-wrap gap-2.5 pt-1">
                  {ACCENT_SWATCHES.map((hex) => {
                    const selected = (form.brand_color || "").toUpperCase() === hex;
                    return (
                      <button
                        key={hex}
                        type="button"
                        aria-label={hex}
                        onClick={() => setForm({ ...form, brand_color: hex })}
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center ring-offset-2 transition-shadow",
                          selected ? "ring-2 ring-ink" : "hover:ring-2 hover:ring-line"
                        )}
                        style={{ backgroundColor: hex }}
                      >
                        {selected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[12px] text-muted2 pt-1">
                  Applies everywhere the moment you save — buttons, links, focus rings and chart accents.
                </p>
              </div>

              <Separator className="my-5" />

              <div className="space-y-1.5">
                <Label>Font</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {FONTS.map((f) => {
                    const selected = (form.font_family || "Plus Jakarta Sans") === f.name;
                    return (
                      <button
                        key={f.name}
                        type="button"
                        onClick={() => setForm({ ...form, font_family: f.name })}
                        className={cn(
                          "rounded-xl border p-2.5 text-left transition-colors",
                          selected ? "border-brand bg-brand-light" : "border-line hover:border-brand/40"
                        )}
                      >
                        <div className="text-[16px] leading-none text-ink" style={{ fontFamily: `'${f.name}', sans-serif` }}>
                          Aa 123
                        </div>
                        <div className="text-[11px] font-medium text-ink mt-1.5 truncate">{f.name}</div>
                        <div className="text-[10px] text-muted2 truncate">{f.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Separator className="my-5" />

              <div className="flex items-start gap-3">
                <Switch
                  checked={!!form.dark_mode}
                  onCheckedChange={(v) => setForm({ ...form, dark_mode: v })}
                  className="mt-0.5 shrink-0"
                />
                <div>
                  <div className="text-[13.5px] font-medium text-ink">Dark mode</div>
                  <p className="text-[12px] text-muted2 mt-0.5">Switches the whole app to a dark palette.</p>
                </div>
              </div>
            </Section>

            <Section
              title="PIN sign-in"
              hint="How strict a PIN must be for anyone on your team you've allowed to use one instead of their password. Changing this clears any PINs already set, so everyone picks one that fits the new rule."
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="pin-length">PIN length</Label>
                  <Input
                    id="pin-length"
                    type="number"
                    min={4}
                    max={12}
                    value={form.pin_policy?.length ?? 7}
                    onChange={(e) => {
                      const length = Math.min(12, Math.max(4, Number(e.target.value) || 4));
                      setForm((f) => ({
                        ...f,
                        pin_policy: {
                          length,
                          min_letters: Math.min(f.pin_policy?.min_letters ?? 0, length),
                        },
                      }));
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pin-letters">Letters required</Label>
                  <Input
                    id="pin-letters"
                    type="number"
                    min={0}
                    max={form.pin_policy?.length ?? 7}
                    value={form.pin_policy?.min_letters ?? 0}
                    onChange={(e) => {
                      const length = form.pin_policy?.length ?? 7;
                      const min_letters = Math.min(length, Math.max(0, Number(e.target.value) || 0));
                      setForm((f) => ({ ...f, pin_policy: { length, min_letters } }));
                    }}
                  />
                </div>
              </div>
              <p className="text-[12px] text-muted2 mt-2.5">
                {(form.pin_policy?.min_letters ?? 0) === 0
                  ? `Right now: ${form.pin_policy?.length ?? 7} digits, numbers only.`
                  : `Right now: ${form.pin_policy?.length ?? 7} characters, at least ${form.pin_policy?.min_letters} letter${
                      form.pin_policy?.min_letters === 1 ? "" : "s"
                    } mixed in.`}
              </p>
              <p className="text-[12px] text-muted2 mt-1">
                Turn PIN sign-in on for individual people from the Team page.
              </p>
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="storage" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Framed as ownership and portability rather than insurance. A client
                shouldn't be left wondering whether their data is at risk — that's
                the operator's concern, and the reminder for it is owner-only. */}
            <Section
              title="Go back in time"
              hint="A copy is saved automatically before anything that wipes data. You can also take one yourself before making big changes."
            >
              <Button variant="outline" onClick={takeSnapshot} className="w-full">
                <History className="w-4 h-4 mr-2" /> Save a copy of right now
              </Button>

              {snapshots.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {snapshots.map((snap) => {
                    const look = SNAPSHOT_LOOK[snap.reason] || SNAPSHOT_LOOK.manual;
                    const Icon = look.icon;
                    const c = snap.counts || {};
                    const diff = describeChanges(snap.changes);
                    return (
                      <div
                        key={snap.snapshot_id}
                        className="rounded-xl border border-line px-3.5 py-3 flex items-start gap-3"
                      >
                        <span
                          className={cn(
                            "w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                            look.tone
                          )}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium text-ink">
                            {snap.reason_label}
                          </div>
                          <div className="text-[11.5px] text-muted2 mt-0.5">
                            {timeAgo(snap.at)}
                          </div>
                          <div className="text-[11.5px] text-muted2 font-mono mt-1.5">
                            {c.conversations || 0} conversations · {c.messages || 0} messages
                          </div>
                          {/* The line that actually decides which one you want. */}
                          {diff && <div className="text-[11.5px] text-gold mt-1">{diff}</div>}
                        </div>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restoreSnapshot(snap)}
                          className="shrink-0"
                        >
                          Restore
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[12px] text-muted2 mt-3">
                  No copies yet. One is saved automatically the first time something
                  destructive happens.
                </p>
              )}
            </Section>

            <Section
              title="Export your data"
              hint="Your conversations, messages and settings in one file. It's your data — take a copy whenever you like."
            >
              <Button variant="outline" onClick={downloadBackup} className="w-full">
                <Download className="w-4 h-4 mr-2" /> Download my data
              </Button>
              <p className="text-[12px] text-muted2 mt-3">
                Handy for your own records, or before making a big change.
              </p>
            </Section>

            <Section
              title="Import data"
              hint="Replace everything here with a previously exported backup file. A copy of what's here now is saved first, so it's reversible from Go back in time."
            >
              <input
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={importBackup}
              />
              <Button
                variant="outline"
                onClick={() => importInputRef.current?.click()}
                disabled={importing}
                className="w-full"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
                Import data…
              </Button>
            </Section>

            <Section title="Danger zone" hint="Irreversible. Think twice.">
              <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
                <div className="text-[13.5px] font-medium text-ink">Clear all data</div>
                <p className="text-[12.5px] text-muted2 mt-1">
                  Removes every conversation and message. Team accounts are kept.
                </p>
                <Button
                  variant="outline"
                  onClick={resetAll}
                  className="mt-3 border-rose-200 text-stock-out hover:bg-rose-100 hover:text-stock-out"
                >
                  Clear everything
                </Button>
              </div>
            </Section>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
